import React, { memo, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { useTracker } from "@dpdgroupuk/react-event-tracker";

import Request from "./Request";
import PopoverMenu, { LoadTimeMenuItems, StatusMenuItems } from "./PopoverMenu";
import useClick from "../../../../hooks/useClick";
import colors from "../../../styles/palette";
import { View, Text } from "../../../Base";
import Forecasts from "./Forecasts";
import {
  formatLoadTimeData,
  isRouteStatusDeparted,
  isRouteStatusDownloaded,
  isRouteStatusInBuild,
  isRouteStatusLoading,
} from "../../../../models/routeDetail";
import {
  LoadTimeIcon,
  RedLoadTimeIcon,
  RouteStatusIcon,
  RouteStatusLockedIcon,
  ScanTimeIcon,
  WarningLoadTimeIcon,
} from "../../../icons";
import palette from "../../../styles/palette";
import { ROUTES } from "../../../../constants/analytics";

const useStyles = makeStyles(() => ({
  section: {
    display: "flex",
    flexDirection: "row",
    background: palette.typography.white.neutral,
    padding: "0.625rem",
    paddingBottom: "0.5rem",
    cursor: "default",
  },
  active: {
    background: colors.darkGreen,
  },
  left: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginLeft: "0.313rem",
  },
  header: {
    height: "1.25rem",
    color: "white",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  },
  routeCode: {
    width: "2.5rem",
    height: "1.25rem",
    lineHeight: "1.25rem",
    fontSize: "0.75rem",
    textAlign: "center",
    borderRadius: "0.125rem",
    fontWeight: "bold",
    background: "green",
    color: "white",
    marginBottom: "9px",
    cursor: "pointer",
    position: "relative",
  },
  shape: {
    position: "absolute",
    width: "0.25rem",
    height: "0.25rem",
    borderWidth: "0 0.094rem 0.094rem 0",
    borderColor: "white",
    borderStyle: "solid",
    bottom: "0.125rem",
    right: "0.125rem",
  },
}));

const BaseCard = ({
  spot,
  children,
  active,
  onDoubleClick,
  onClick,
  onRouteCodeClick,
  onStatusChange,
  route,
  detailedStatus,
}) => {
  const styles = useStyles();
  const tracker = useTracker();

  const {
    routeId,
    routeCode,
    routeStatus,
    routeLockedBy,
    totalOpenRequests,
    driverName,
    driverCode,
    totalForecastStops,
    totalForecastParcels,
    exceededLoadTime,
    lowLoadRate,
    scheduleRequired,
  } = route;

  const routeStatusInBuild = useMemo(
    () => isRouteStatusInBuild({ status: routeStatus }),
    [routeStatus]
  );

  const loadTimeIcon = useMemo(() => {
    if (isRouteStatusDownloaded({ status: routeStatus })) {
      return <ScanTimeIcon />;
    }
    if (
      isRouteStatusLoading({ status: routeStatus }) ||
      isRouteStatusDeparted({ status: routeStatus })
    ) {
      if (exceededLoadTime) {
        return <WarningLoadTimeIcon />;
      }
      return isRouteStatusDeparted({ status: routeStatus }) || !lowLoadRate ? (
        <LoadTimeIcon />
      ) : (
        <RedLoadTimeIcon />
      );
    }
    return null;
  }, [exceededLoadTime, lowLoadRate, routeStatus]);

  const statusChange = useCallback(
    (status) => {
      onStatusChange(status, routeId, routeStatus);
    },
    [onStatusChange, routeId, routeStatus]
  );

  const handleClick = useCallback(() => {
    onClick(route);
  }, [onClick, route]);

  const handleDoubleClick = useCallback(() => {
    onDoubleClick(route);
  }, [onDoubleClick, route]);

  const click = useClick(handleClick, handleDoubleClick);

  const { statusIcon, onClickStatus } = useMemo(
    () =>
      routeLockedBy
        ? {
            statusIcon: (
              <View data-testid="route-icon-status-locked">
                <RouteStatusLockedIcon />
              </View>
            ),
            onClickStatus: () => tracker.logEvent(ROUTES.CLICK_LOCK_ICON),
          }
        : {
            statusIcon: (
              <View data-testid="route-icon-status">
                <RouteStatusIcon />
              </View>
            ),
            onClickStatus: () =>
              tracker.logEvent(ROUTES.CLICK_ROUTE_STATUS_ICON),
          },
    [routeLockedBy, tracker]
  );

  const handleRouteCodeClick = useCallback(
    (e) => {
      if (!routeLockedBy && onRouteCodeClick) {
        e.stopPropagation();
        onRouteCodeClick(route);
      }

      tracker.logEvent(ROUTES.SELECT_ROUTE_NUMBER);
    },
    [routeLockedBy, onRouteCodeClick, route, tracker]
  );

  const renderPopover = useCallback(
    ({ handleMenuItemClick, setOpen }) => (
      <StatusMenuItems
        onItemClick={handleMenuItemClick}
        setOpen={setOpen}
        routeLockedBy={routeLockedBy}
        routeStatus={routeStatus}
        driverCode={driverCode}
        scheduleRequired={scheduleRequired}
      />
    ),
    [routeLockedBy, routeStatus, driverCode, scheduleRequired]
  );

  const formattedLoadTimeData = useMemo(() => formatLoadTimeData(route), [
    route,
  ]);

  const renderLoadTimePopover = useCallback(() => {
    return <LoadTimeMenuItems items={formattedLoadTimeData} />;
  }, [formattedLoadTimeData]);

  return (
    <View
      onClick={click}
      className={clsx(styles.section, active && styles.active)}
    >
      <View className={styles.left}>
        <View
          style={{ background: spot }}
          onClick={handleRouteCodeClick}
          className={styles.routeCode}
        >
          {routeCode}
          <View className={styles.shape} />
        </View>
        {routeStatusInBuild && detailedStatus && (
          <Forecasts
            stops={totalForecastStops}
            parcels={totalForecastParcels}
          />
        )}
        {!!totalOpenRequests && <Request value={totalOpenRequests} />}
        {detailedStatus && loadTimeIcon && (
          <PopoverMenu
            icon={loadTimeIcon}
            placement="bottom-start"
            render={renderLoadTimePopover}
          />
        )}
        {detailedStatus && (
          <PopoverMenu
            icon={statusIcon}
            onChange={statusChange}
            placement="right-end"
            render={renderPopover}
            onOpenPopover={onClickStatus}
            key={routeId}
          />
        )}
      </View>
      <View className={styles.right}>
        <Text className={styles.header} data-testid="driver-name">
          {driverName}
        </Text>
        {children}
      </View>
    </View>
  );
};

export default memo(BaseCard);
